import React, { memo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import CarStats from "./CarStats/CarStats";
import VehicleVisualizer from "./VehicleVisualizer/VehicleVisualizer";
import LightingManager from "./LightingManager/LightingManager";
import WindowsManager from "./WindowsManager/WindowsManager";
import type { Door, CarPanel as PanelType, Light, CarWindow } from "./types";
import { useAudioEffects } from "../../../../hooks/use-audio-effects";
import closeDoorSound from "../../assets/audio/close-door.mp3";
import openDoorSound from "../../assets/audio/open-door.mp3";
import lockCarSound from "../../assets/audio/lock-car.mp3";
import windowMoveSound from "../../assets/audio/window-move.mp3";

// ─── Initial state ────────────────────────────────────────────────────────────

const INITIAL_DOORS: Door[] = [
  { id: "fl", label: "Front Left", isOpen: true, windowIsDown: false },
  { id: "fr", label: "Front Right", isOpen: true, windowIsDown: false },
  { id: "rl", label: "Rear Left", isOpen: false, windowIsDown: true },
  { id: "rr", label: "Rear Right", isOpen: false, windowIsDown: true },
];

const INITIAL_PANELS: PanelType[] = [
  { id: "hood", label: "Hood", isOpen: false },
  { id: "trunk", label: "Trunk", isOpen: false },
];

const INITIAL_LIGHTS: Light[] = [
  { id: "headlights", name: "Switch name", subName: "Window name", isOn: true },
  { id: "hazard", name: "Switch name", subName: "Window name", isOn: false },
  { id: "fog", name: "Switch name", subName: "Window name", isOn: true },
  { id: "interior", name: "Switch name", subName: "Window name", isOn: false },
  { id: "ambient", name: "Switch name", subName: "Window name", isOn: true },
];

const INITIAL_WINDOWS: CarWindow[] = [
  { id: "fl", name: "Window name", isOpen: true },
  { id: "fr", name: "Window name", isOpen: true },
  { id: "rl", name: "Window name", isOpen: false },
  { id: "rr", name: "Window name", isOpen: false },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface CarPanelProps {
  className?: string;
  carName?: string;
  fuel?: number;
  health?: number;
}

const CarPanel: React.FC<CarPanelProps> = memo(
  ({
    className = "",
    carName = "Volkswagen Golf 7.5 GTI",
    fuel = 68,
    health = 68,
  }) => {
    const [doors, setDoors] = useState<Door[]>(INITIAL_DOORS);
    const [panels, setPanels] = useState<PanelType[]>(INITIAL_PANELS);
    const [lights, setLights] = useState<Light[]>(INITIAL_LIGHTS);
    const [windows, setWindows] = useState<CarWindow[]>(INITIAL_WINDOWS);

    const { playEffect } = useAudioEffects();

    // ── Door handlers ──────────────────────────────────────────────────────────

    const toggleDoor = useCallback((id: Door["id"]) => {
      setDoors((prev) => {
        const door = prev.find((d) => d.id === id);
        if (door)
          playEffect(door.isOpen ? closeDoorSound : openDoorSound, {
            volume: 0.7,
          });
        return prev.map((d) => (d.id === id ? { ...d, isOpen: !d.isOpen } : d));
      });
    }, []);

    const togglePanel = useCallback((id: PanelType["id"]) => {
      setPanels((prev) => {
        const panel = prev.find((p) => p.id === id);
        if (panel)
          playEffect(panel.isOpen ? closeDoorSound : openDoorSound, {
            volume: 0.7,
          });
        return prev.map((p) => (p.id === id ? { ...p, isOpen: !p.isOpen } : p));
      });
    }, []);

    const openAllDoors = useCallback(() => {
      playEffect(lockCarSound, { volume: 0.7 });
      setDoors((prev) => prev.map((d) => ({ ...d, isOpen: true })));
    }, []);

    const closeAllDoors = useCallback(() => {
      playEffect(lockCarSound, { volume: 0.7 });
      setDoors((prev) => prev.map((d) => ({ ...d, isOpen: false })));
    }, []);

    // ── Light handler ──────────────────────────────────────────────────────────

    const toggleLight = useCallback((id: string) => {
      setLights((prev) =>
        prev.map((l) => (l.id === id ? { ...l, isOn: !l.isOn } : l)),
      );
    }, []);

    // ── Window handlers ────────────────────────────────────────────────────────

    const windowUp = useCallback((id: string) => {
      playEffect(windowMoveSound, { volume: 0.8 });
      setWindows((prev) =>
        prev.map((w) => (w.id === id ? { ...w, isOpen: true } : w)),
      );
    }, []);

    const windowDown = useCallback((id: string) => {
      playEffect(windowMoveSound, { volume: 0.8 });
      setWindows((prev) =>
        prev.map((w) => (w.id === id ? { ...w, isOpen: false } : w)),
      );
    }, []);

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
      <div className={`flex-1 min-h-0 flex flex-col gap-4 ${className}`}>
        {/* ── Row 1: Car identity + stats ── */}
        <motion.div
          className="flex items-center justify-between flex-shrink-0 border-b-[1px] border-white/5 pb-4 pt-4"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <div>
            <p className="text-white/50 text-[14px] font-[400] tracking-wide">
              Management of Car
            </p>
            <h2 className="text-white text-[20px] font-[400] leading-tight mt-0.5">
              {carName}
            </h2>
          </div>
          <CarStats fuel={fuel} health={health} />
        </motion.div>

        {/* ── Row 2: Visualizer + Lighting ── */}
        <motion.div
          className="flex gap-4 flex-1 min-h-0 fl"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.1 }}
        >
          <VehicleVisualizer
            doors={doors}
            className="flex-[2]"
            panels={panels}
            onDoorToggle={toggleDoor}
            onPanelToggle={togglePanel}
            onOpenAll={openAllDoors}
            onCloseAll={closeAllDoors}
          />
          <LightingManager
            lights={lights}
            onToggle={toggleLight}
            className="flex-1"
          />
        </motion.div>

        {/* ── Row 3: Windows ── */}
        <motion.div
          className="flex-shrink-0"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.2 }}
        >
          <WindowsManager
            windows={windows}
            onWindowUp={windowUp}
            onWindowDown={windowDown}
          />
        </motion.div>
      </div>
    );
  },
);

CarPanel.displayName = "CarPanel";
export default CarPanel;
